import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { betTitle, type SpecialBetRow } from "@/components/SpecialBetsTab";

interface Props {
  leagueId: string;
  tournamentId: string | null;
}

/** Per-league on/off + points override for each special bet of the tournament. */
export const LeagueSpecialBetsSettings = ({ leagueId, tournamentId }: Props) => {
  const { t, i18n } = useTranslation();
  const [bets, setBets] = useState<SpecialBetRow[]>([]);
  const [rows, setRows] = useState<Record<string, { enabled: boolean; points: number }>>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (!tournamentId) { setLoading(false); return; }
      setLoading(true);
      const [{ data: b }, { data: ls }] = await Promise.all([
        supabase.rpc("get_special_bets", { _tournament_id: tournamentId }),
        supabase.from("league_special_bets").select("bet_id,enabled,points_override").eq("league_id", leagueId),
      ]);
      if (cancelled) return;
      const betList = (b as SpecialBetRow[]) ?? [];
      const overrides: Record<string, any> = {};
      (ls ?? []).forEach((x: any) => { overrides[x.bet_id] = x; });
      const map: Record<string, { enabled: boolean; points: number }> = {};
      betList.forEach((bet) => {
        map[bet.id] = {
          enabled: overrides[bet.id]?.enabled ?? true,
          points: overrides[bet.id]?.points_override ?? bet.default_points,
        };
      });
      setBets(betList);
      setRows(map);
      setLoading(false);
    };
    run();
    return () => { cancelled = true; };
  }, [leagueId, tournamentId]);

  const persist = async (betId: string, next: { enabled: boolean; points: number }) => {
    setRows((r) => ({ ...r, [betId]: next }));
    const { error } = await supabase
      .from("league_special_bets")
      .upsert({ league_id: leagueId, bet_id: betId, enabled: next.enabled, points_override: next.points },
        { onConflict: "league_id,bet_id" });
    if (error) toast.error(error.message);
  };

  const sorted = useMemo(() => bets, [bets]);

  if (loading) return <p className="text-xs text-muted-foreground">{t("common.loading")}</p>;
  if (sorted.length === 0) return <p className="text-xs text-muted-foreground">{t("specialBets.none")}</p>;

  return (
    <div className="space-y-2">
      {sorted.map((b) => {
        const row = rows[b.id];
        return (
          <div key={b.id} className="flex items-center justify-between gap-3 rounded-xl border border-border bg-card/50 p-3">
            <div className="min-w-0">
              <p className="truncate text-sm font-medium">{betTitle(b, i18n.language)}</p>
              <p className="text-xs text-muted-foreground">{t("specialBets.pointsLabel")}</p>
            </div>
            <div className="flex shrink-0 items-center gap-2">
              <Input
                type="number"
                min={0}
                value={row?.points ?? b.default_points}
                onChange={(e) => persist(b.id, { enabled: row?.enabled ?? true, points: Math.max(0, parseInt(e.target.value) || 0) })}
                className="h-9 w-16 rounded-xl"
                disabled={!(row?.enabled ?? true)}
                aria-label={t("specialBets.pointsLabel")}
              />
              <Switch
                checked={row?.enabled ?? true}
                onCheckedChange={(v) => persist(b.id, { enabled: v, points: row?.points ?? b.default_points })}
              />
            </div>
          </div>
        );
      })}
      <Label className="text-[11px] text-muted-foreground">{t("specialBets.settingsHint")}</Label>
    </div>
  );
};
